import { useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import { Option, OptionsList } from 'modules/core/components/DateNavigator/_OptionsList'
import { Popover } from 'modules/core/components/DateNavigator/_Popover'
import { Cycle } from 'modules/core/components/DateNavigator/cycleUtils'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import * as styles from './index.vanilla.css'

type Props = {
  cycle: Cycle
  onCycleChange?: (cycle: Cycle) => void
}

export const CycleSelector = ({ cycle, onCycleChange }: Props) => {
  const [popoverOpen, setPopoverOpen] = useState(false)

  const handleOptionSelected = useUnsafeCallback(
    (newCycle: Cycle) => {
      onCycleChange?.(newCycle)
      setPopoverOpen(false)
    },
    [onCycleChange]
  )

  const handleOpenPopover = useUnsafeCallback(() => {
    setPopoverOpen(true)
  }, [])

  const handleClosePopover = useUnsafeCallback(() => {
    setPopoverOpen(false)
  }, [])

  const options = useMemo<Option<Cycle>[]>(
    () => [
      {
        label: i18n.t('common.date_navigator.cycles.daily'),
        value: 'daily',
      },
      {
        label: i18n.t('common.date_navigator.cycles.weekly'),
        value: 'weekly',
      },
      {
        label: i18n.t('common.date_navigator.cycles.monthly'),
        value: 'monthly',
      },
      {
        label: i18n.t('common.date_navigator.cycles.quarterly'),
        value: 'quarterly',
      },
    ],
    []
  )

  const selectedOption = options.find((option) => option.value === cycle)

  return (
    <Popover
      open={popoverOpen}
      onClose={handleClosePopover}
      trigger={
        <Box
          className={styles.clickable}
          flexDirection="row"
          gap="s4"
          height="s32"
          paddingLeft="s6"
          paddingRight="s8"
          alignItems="center"
          justifyContent="center"
          border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
          borderRadius={{ all: 'abs008' }}
          onClick={handleOpenPopover}
        >
          <Icon.Medium icon="calendar" color="grey600" />
          <Text.Normal color="grey1000" size="200">
            {selectedOption?.label}
          </Text.Normal>
        </Box>
      }
    >
      <OptionsList
        options={options}
        selectedOptionValue={selectedOption?.value}
        onOptionSelected={handleOptionSelected}
      />
    </Popover>
  )
}
